import React, { useCallback, useMemo, useState } from "react";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch as Switcher,
  Typography
} from "@mui/material";
import EmptyCardDataOperationsData from "assets/images/EmptyCardDataOperationsData.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { RoleComponentGuard } from "components/guards";
import { Menu } from "components/popups";
import {
  useDealActions,
  useDealSelector,
  useMonitoringDataFilters,
  usePickUserPermissions,
  useViewActions
} from "store";
import { MeasureDataTableFilters, UserPermissionType } from "store/types";

import { RoleName } from "../../../../constants";
import { DraftsTable } from "../../Drafts-table/DraftsTable";
import { SelectDeal } from "../../Select-deal/SelectDeal";
import { DataFilters } from "../Data-filters/DataFilters";
import { DataTable } from "../Data-table/DataTable";

const dataFiltersOptions = [
  {
    title: "Active",
    key: "active"
  },
  {
    title: "Draft",
    key: "draft"
  },
  {
    title: "Deleted Items",
    key: "deleted"
  }
];

interface TableComponentProps {
  setFilters: (data: Partial<MeasureDataTableFilters> | null) => void;
  showTopInvestors: boolean;
  setShowTopInvestors: (value: boolean) => void;
  dealId?: number;
}

export const TableComponent: React.FC<TableComponentProps> = ({
  setFilters,
  showTopInvestors,
  setShowTopInvestors,
  dealId
}) => {
  const filters = useMonitoringDataFilters();
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const noAccessPermission = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;

  const [selectedTable, setSelectedTable] = useState<string>("active");
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isSelectDeal, setIsSelectDeal] = useState<((id: number, title: string) => void) | null>(null);

  const { downloadDataAction } = useDealActions();
  const { setCreateManyMeasureData } = useViewActions();
  const deal = useDealSelector();

  const onDownloadData = useCallback(
    ({ hideInvestors }: { hideInvestors: boolean }) => {
      if (dealId) {
        return downloadDataAction({ deal_ids: [dealId], filters, hideInvestors });
      }

      const selectedDeals: number[] = filters.deals.map((data) => {
        return data.id;
      });
      downloadDataAction({ deal_ids: selectedDeals, filters, hideInvestors });
    },
    [dealId, filters]
  );

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedTable(event.target.value);
  };

  const handleAddValue = useCallback(() => {
    if (deal.id) {
      setCreateManyMeasureData({ dealId: deal.id, dealTitle: deal.title });
      return;
    }
    setIsSelectDeal(() => async (id: number, title: string) => {
      setCreateManyMeasureData({ dealId: id, dealTitle: title });
    });
  }, [setCreateManyMeasureData, deal]);

  const renderTable = useMemo(() => {
    if (selectedTable === "active" || selectedTable === "deleted") {
      return (
        <DataTable
          showTopInvestors={showTopInvestors}
          setLoading={setIsLoading}
          setEmpty={setIsEmpty}
          dealId={dealId}
          onlyDeleted={selectedTable === "deleted"}
        />
      );
    }
    if (selectedTable === "draft") {
      return <DraftsTable setIsEmpty={setIsEmpty} />;
    }
  }, [selectedTable, showTopInvestors, dealId]);

  const showEmptyCard = isEmpty && selectedTable !== "draft";

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
    >
      <Stack sx={{ bgcolor: "white", borderRadius: "8px", padding: "16px", overflow: "auto", flexGrow: 1 }}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: "16px", width: "100%", bgcolor: "white", borderRadius: "8px" }}
        >
          {selectedTable !== "draft" && !showEmptyCard ? (
            <DataFilters disabled={isLoading} filters={filters} setFilters={setFilters} />
          ) : (
            <Stack></Stack>
          )}
          <Stack flexDirection="row" alignItems="center" justifyContent="flex-end" gap="6px">
            <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="10px">
              <Typography>Show</Typography>
              <FormControl>
                <Select
                  sx={{
                    width: "140px",
                    padding: "0px",
                    ".MuiSelect-select": {
                      padding: "6px"
                    },
                    "& > fieldset": { borderColor: "rgba(123, 146, 169, 1)", borderRadius: "10px" }
                  }}
                  value={selectedTable}
                  onChange={handleChange}
                >
                  {dataFiltersOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {!isEmpty && (
              <Menu
                options={[
                  {
                    title: "Export With Investors",
                    icon: <FileDownloadIcon />,
                    onClick: () => onDownloadData({ hideInvestors: false })
                  },
                  {
                    title: "Export Without Investors",
                    icon: <FileDownloadIcon />,
                    onClick: () => onDownloadData({ hideInvestors: true })
                  },
                  () => (
                    <MenuItem>
                      <FormControlLabel
                        control={
                          <Switcher
                            checked={showTopInvestors}
                            onChange={(e) => {
                              setShowTopInvestors(e.target.checked);
                              // refetch();
                            }}
                          />
                        }
                        label={showTopInvestors ? "Hide Entity Hierarchy" : "Show Entity Hierarchy"}
                      />
                    </MenuItem>
                  )
                ]}
              />
            )}
          </Stack>
        </Stack>
        {isLoading && isEmpty ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
            <CircularProgress />
          </Stack>
        ) : showEmptyCard ? (
          <EmptyCard
            title={
              selectedTable === "deleted"
                ? EMPTY_CARD_CONTENT.dataOperationsDeleted.title
                : EMPTY_CARD_CONTENT.dataOperationsData.title
            }
            description={{
              text:
                selectedTable === "deleted"
                  ? EMPTY_CARD_CONTENT.dataOperationsDeleted.description
                  : EMPTY_CARD_CONTENT.dataOperationsData.description,
              externalText:
                selectedTable === "deleted"
                  ? EMPTY_CARD_CONTENT.dataOperationsDeleted.externalDescription
                  : EMPTY_CARD_CONTENT.dataOperationsData.externalDescription
            }}
            image={{
              src: EmptyCardDataOperationsData,
              sx: { width: 315, height: 140 }
            }}
            fitPage
            {...(selectedTable !== "deleted" && {
              actionsElement: (
                <Button variant="contained" onClick={handleAddValue} startIcon={<PostAddIcon />} sx={{ bgcolor: "#071F35" }}>
                  Add Value
                </Button>
              )
            })}
          />
        ) : null}
        <Stack
          sx={{ display: (isLoading && isEmpty) || showEmptyCard ? "none" : "flex", flexGrow: 1, minHeight: 0 }}
        >
          {renderTable}
        </Stack>
      </Stack>
      <SelectDeal onSubmit={isSelectDeal} onCancel={() => setIsSelectDeal(null)} />
    </RoleComponentGuard>
  );
};
